import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { Flex } from 'rebass/styled-components';

import { layout } from '../../../constants/constants';
import { Media } from '../../contexts/media/MediaContext';
import { IconPack } from '../../models/app-data-model';
import ShIcon from '../sh-icon/sh-icon';
import './sh-slider.scss';

export type ShSliderProps = {
  numberOfCards: number;
  children: ReactNode;
  iconPack: IconPack[];
  hideBackground?: boolean;
  slideWidthInPixels?: number;
};

type SliderIndex = number | 'previous' | 'next';

const ShSlider: React.FC<ShSliderProps> = ({ numberOfCards, iconPack, hideBackground, children, slideWidthInPixels }: ShSliderProps) => {
  const [xPos, setXPos] = useState(0);
  const sliderIncrement = slideWidthInPixels || 100;
  const sliderIncrementUnit = slideWidthInPixels ? 'px' : '%';
  const [translate, setTranslate] = useState({ transform: `translateX(${xPos}${sliderIncrementUnit})` });
  const [bubblePosition, setBubblePosition] = useState(0);
  const maxScrollPositionX = sliderIncrement * (numberOfCards - 1);
  const backgroundClass = hideBackground ? '' : 'slider-background';
  const navBubbles: JSX.Element[] = [];

  for (let index = 0; numberOfCards > 1 && index < numberOfCards; index++) {
    navBubbles.push(
      <button
        key={`nav-bubble-${index}`}
        className={`nav-bubble unstyled-btn ${index === bubblePosition ? 'active' : ''}`}
        data-testid={`bubble-${index}`}
        onClick={() => goToSlide(index)}
        aria-label={`Slider navigation bubble ${index + 1} of ${numberOfCards}`}
      ></button>
    );
  }

  const goToSlide = useCallback(
    (index: SliderIndex): void => {
      if (index === 'previous' && xPos < 0) {
        setXPos((x) => x + sliderIncrement);

        // istanbul ignore else
        if (bubblePosition > 0) {
          setTimeout(() => setBubblePosition(bubblePosition - 1), 50);
        }
      } else if (index === 'next' && xPos !== -maxScrollPositionX) {
        setXPos((x) => x - sliderIncrement);

        // istanbul ignore else
        if (bubblePosition < numberOfCards) {
          setTimeout(() => setBubblePosition(bubblePosition + 1), 50);
        }
      } else if (typeof index === 'number' && index >= 0) {
        setXPos(-sliderIncrement * index);
        setBubblePosition(index);
      }
    },
    [bubblePosition, maxScrollPositionX, numberOfCards, sliderIncrement, xPos]
  );

  const gestureHandlers = useSwipeable({
    onSwipedLeft: () => goToSlide('next'),
    onSwipedRight: () => goToSlide('previous'),
    swipeDuration: 500,
    preventScrollOnSwipe: true,
    trackMouse: true
  });

  useEffect(() => {
    setTranslate({ transform: `translateX(${xPos}${sliderIncrementUnit})` });
    setBubblePosition(bubblePosition);
  }, [bubblePosition, sliderIncrementUnit, xPos]);

  const navButtons = (
    <>
      <button
        className={`unstyled-btn arrow-icon-container ${bubblePosition === 0 ? 'disabled' : ''}`}
        onClick={() => goToSlide('previous')}
        data-testid="slider-left-arrow"
        aria-label="Previous"
      >
        <ShIcon iconPack={iconPack} iconName="arrow" className="slider-left-arrow" altText="Back arrow icon" />
      </button>
      <button
        className={`unstyled-btn arrow-icon-container ${bubblePosition === numberOfCards - 1 ? 'disabled' : ''}`}
        onClick={() => goToSlide('next')}
        data-testid="slider-right-arrow"
        aria-label="Next"
      >
        <ShIcon iconPack={iconPack} iconName="arrow" className="slider-right-arrow" altText="Forward arrow icon" />
      </button>
    </>
  );

  return (
    <div className="sh-slider">
      <div className={backgroundClass}>
        <div {...gestureHandlers} className="slider-content" style={translate} data-testid="slider-content">
          {children}
        </div>
      </div>

      <Flex className="slider-navigation" width={layout.singleColWidth}>
        {navButtons}
        <Media greaterThanOrEqual="tablet" className="nav-bubble-container">
          {navBubbles}
        </Media>
      </Flex>
    </div>
  );
};

export default ShSlider;
